import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SeoHelmet from '@/components/base/SeoHelmet';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryMap[slug] : undefined;

  if (!category) {
    return <NotFound />;
  }

  const articles = getArticlesByCategory(category.slug);

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 免费91 网页`,
    description: category.description,
    url: `https://free91.com/category/${category.slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: articles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://free91.com/article/${article.slug}`,
        name: article.title,
      })),
    },
  };

  return (
    <>
      <SeoHelmet
        title={`${category.name} - 免费资源导航`}
        description={category.description}
        keywords={`${category.name},免费${category.name},${category.name}资源,免费在线${category.name},91免费${category.name}网页`}
        canonicalUrl={`https://free91.com/category/${category.slug}`}
        schema={collectionSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
            <div className="flex items-center gap-4 mt-2">
              <div className="w-14 h-14 rounded-2xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className={`${category.icon} text-2xl text-primary-600`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1.5 max-w-2xl">
                  {category.description}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    cat.slug === category.slug
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Articles List */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-600">
                共 <strong className="text-foreground-900">{articles.length}</strong> 篇{category.name}相关文章
              </p>
            </div>

            {articles.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-4xl text-foreground-300 mb-3 block"></i>
                <p className="text-sm text-foreground-500">该分类暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300"
                  >
                    <div className="flex flex-col sm:flex-row">
                      <div className="sm:w-44 h-40 sm:h-auto flex-shrink-0 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="flex-1 p-4 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            {article.featured && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-accent-100 text-accent-700 font-medium">
                                精选
                              </span>
                            )}
                            <span className="text-xs text-foreground-400">{article.readTime}</span>
                          </div>
                          <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                            {article.title}
                          </h3>
                          <p className="text-xs text-foreground-500 mt-1.5 line-clamp-2 leading-relaxed">
                            {article.excerpt}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 mt-3 text-xs text-foreground-400">
                          <span>{article.date}</span>
                          <span>·</span>
                          <span>{article.author}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}